import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AuthStage, cookieStorage } from '.';
import { Method, queryData } from '../../../utilities/fetch';
import { ISignInResponse } from 'api-paths-cas';
import { shouldShowMFASetup } from './mfa-setup/mfa';
import { clearStaleDeviceCookies } from './clearCognitoCookies';

interface Params {
  mfaEnabledInConfig: boolean;
  username: string;
  password: string;
  code?: string;
  user: CognitoUser;
  appClient: string;
  apiUrl: string;
  setPassword: (password: string) => void;
  setAuthStage: (stage: AuthStage) => void;
  setIsLoading: (loading: boolean) => void;
}

function completeNewPassword(user: CognitoUser, password: string, username: string): Promise<CognitoUserSession> {
  const onError = (
    reason: Error & { code?: string },
    resolvePromise: (value: unknown) => void,
    rejectPromise: (reason: Error) => void,
  ): void => {
    // user.confirmPassword() fails when a user has an apostrophe in their username. The confirm password API request
    // is successful and the session data is updated but the Device API request made after fails
    if (reason?.code === 'InvalidParameterException' && username.includes("'")) {
      const session = user.getSignInUserSession();
      if (session) {
        resolvePromise(session);
      } else {
        rejectPromise(new Error('Unable to get session'));
      }
    } else {
      rejectPromise(reason);
    }
  };

  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      password,
      {},
      {
        onSuccess: (session: CognitoUserSession) => resolve(session),
        onFailure: reason => onError(reason, resolve as (value: unknown) => void, reject),
      },
    );
  });
}

function confirmPasswordAsync(user: CognitoUser, code: string, password: string): Promise<CognitoUserSession | string> {
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, password, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export default async function onNewPasswordSubmit(params: Params): Promise<CognitoUserSession | string | undefined> {
  const { mfaEnabledInConfig, username, password, user, code, appClient, apiUrl, setPassword, setAuthStage } = params;

  if (code) {
    const codeSession: CognitoUserSession | string = await confirmPasswordAsync(user, code, password);
    clearStaleDeviceCookies(cookieStorage, appClient, username);
    cookieStorage.setItem('devicesPendingClear', 'true');
    setAuthStage(AuthStage.NONE);
    return codeSession;
  }

  // 1️⃣ Cognito success
  const session: CognitoUserSession | string = await completeNewPassword(user, password, username);

  setPassword('');
  cookieStorage.setItem('username', username);

  // 2️⃣ Call whoami
  const userResult = await queryData<{ clientId: string; token: string; username: string }, ISignInResponse>({
    uri: `/users/whoami`,
    idToken: session.getIdToken().getJwtToken(),
    domain: apiUrl,
    method: Method.Post,
    body: {
      clientId: appClient,
      token: session.getRefreshToken().getToken(),
      username,
    },
  });

  // 3️⃣ Decision logic
  if (!userResult || 'error' in userResult || 'errorType' in userResult) {
    setAuthStage(AuthStage.NONE);
    return session;
  }

  const { isAgentUser = false, agencyMfaEnabled = false } = userResult;

  if (shouldShowMFASetup(mfaEnabledInConfig, isAgentUser, agencyMfaEnabled)) {
    setAuthStage(AuthStage.MFA_SETUP);
    return session;
  }

  setAuthStage(AuthStage.NONE);
  return session;
}
